// HeroSection.js — full-bleed background, consultation-led hero.
// Structure (identical on every breakpoint, per the mobile-first rework):
//   headline → sub-headline → primary CTA (book a consultation) →
//   one program-search field → proof stats. The old three-<select> search
//   card lived here before; program filtering is richer on /programs itself
//   (country, degree, language, price, university type), so the hero now
//   only carries a single "jump to search" field and leads with the real
//   conversion action instead.
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getPageContentFlat, getPartners, getAboutStats } from '../services/api';
import { useConnection } from '../context/ConnectionContext';
import HeroGlobe from './HeroGlobe';
import './HeroSection.css';
import './HeroHeadlineAnimations.css';

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

// Same figures and endpoint as HomeStats / the About page, so the hero's
// proof row never diverges from the rest of the site.
const DEFAULT_STATS = [
  { value: '50+', label: 'Partner universities' },
  { value: '20+', label: 'Students placed' },
];

const HeroSection = () => {
  const navigate = useNavigate();
  const { isLite } = useConnection();
  const [content, setContent] = useState({});
  const [partners, setPartners] = useState([]);
  const [stats, setStats] = useState(DEFAULT_STATS);

  useEffect(() => {
    getPageContentFlat('home')
      .then((flat) => setContent(flat))
      .catch(() => {});
  }, []);

  useEffect(() => {
    getPartners()
      .then(data => setPartners(Array.isArray(data) ? data : (data.results || [])))
      .catch(() => {});
  }, []);

  useEffect(() => {
    getAboutStats()
      .then((list) => {
        if (Array.isArray(list) && list.length > 0) {
          setStats(
            list.slice(0, 3).map((s) => ({
              value: `${s.prefix || ''}${Number(s.value) || s.value || ''}${s.suffix || ''}`,
              label: s.label || '',
            }))
          );
        }
      })
      .catch(() => {});
  }, []);

  // The video is skipped — falling back to the zero-byte animated SVG
  // background — on a slow/metered connection (isLite: a background video
  // is a far bigger download than the photo carousel it replaced ever
  // was) and when the visitor has asked for reduced motion. Neither check
  // needs to be reactive to later changes: isLite already re-renders this
  // component when it flips, and reduced-motion preference doesn't change
  // mid-session in practice.
  const videoUrl = (content.hero_background_video_url || '').trim();
  const showVideo = Boolean(videoUrl) && !isLite && !prefersReducedMotion();

  // ── Primary CTA — the real conversion action. CMS-controlled (Website
  //    Admin → Home Hero → "CTA button text" / "CTA button link"), booking
  //    a consultation by default. ──────────────────────────────────────────
  const primaryCtaText = (content.hero_cta_text || '').trim() || 'Book a free consultation';
  const primaryCtaLink = (content.hero_cta_link || '').trim() || '/consultation';
  const primaryCtaExternal = /^https?:\/\//.test(primaryCtaLink);

  // ── Single program-search field ───────────────────────────────────────
  const [query, setQuery] = useState('');
  const handleSearch = (e) => {
    e.preventDefault();
    const q = query.trim();
    navigate(q ? `/programs?search=${encodeURIComponent(q)}` : '/programs');
  };

  const partnerLogos = partners
    .map(p => ({ name: p.name, logo: p.logo_url || '' }))
    .filter(p => p.logo);

  return (
    <div className="hero-section-wrapper">

      {/* ── Main hero ─────────────────────────────────────── */}
      <section className="hero-main">

        {/* Background — full-bleed video when one is configured (Website
            Admin → Home Hero → Background video), the lightweight animated
            globe otherwise. Never both at once. */}
        <div className="hero-video-bg" aria-hidden="true">
          {showVideo ? (
            <video
              key={videoUrl}
              className="hero-video"
              autoPlay
              muted
              loop
              playsInline
            >
              <source src={videoUrl} type="video/mp4" />
            </video>
          ) : (
            <HeroGlobe />
          )}
          <div className="hero-video-overlay" />
        </div>

        {/* Content — capped width, biased toward the left from 769px up */}
        <div className="hero-left">
          <div className="hero-headline">
            <h1
              className={`hero-title${content.hero_title_animation ? ` hero-anim-${content.hero_title_animation}` : ''}`}
            >
              {content.hero_title || 'Your Global Future Starts Here.'}
            </h1>
            <p className="hero-subtitle">
              {content.hero_subtitle || 'Recognised partner universities, with expert help at every step.'}
            </p>
          </div>

          {/* Primary CTA + one search field */}
          <div className="hero-actions">
            {primaryCtaExternal ? (
              <a href={primaryCtaLink} className="hero-cta-primary">
                {primaryCtaText} &rarr;
              </a>
            ) : (
              <Link to={primaryCtaLink} className="hero-cta-primary">
                {primaryCtaText} &rarr;
              </Link>
            )}

            <form onSubmit={handleSearch} className="hero-search" role="search">
              <label htmlFor="hero-search-input" className="sr-only">
                Search programmes
              </label>
              <input
                id="hero-search-input"
                type="search"
                className="hero-search-input"
                placeholder="Search programmes by subject"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                autoComplete="off"
              />
              <button type="submit" className="hero-search-btn" aria-label="Search programmes">
                <span aria-hidden="true">&rarr;</span>
                <span className="hero-search-btn-label">Search</span>
              </button>
            </form>
          </div>

          {/* Proof row — same numbers as HomeStats / About */}
          {stats.length > 0 && (
            <ul className="hero-proof" aria-label="Why families choose us">
              {stats.map((s, i) => (
                <li key={i} className="hero-proof-item">
                  <span className="hero-proof-value">{s.value}</span>
                  <span className="hero-proof-label">{s.label}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

      </section>

      {/* ── Trust signals — only rendered when there are real logos to
          show; a bare "Trusted by…" line with nothing under it reads as
          broken. ─────────────────────────────────────────────────────── */}
      {partnerLogos.length > 0 && (
        <section className="trust-signals">
          <div className="trust-inner">
            <p className="trust-text">
              {content.trust_partners_text || (
                <>Trusted by <span className="trust-highlight">50+ Partners</span></>
              )}
            </p>
            <div className="logo-strip">
              {partnerLogos.map((partner, index) => (
                <div key={index} className="partner-logo">
                  <img src={partner.logo} alt={partner.name} width={80} height={40} decoding="async" />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

    </div>
  );
};

export default HeroSection;
